import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth'
import { Loading } from './components'
import { AdminLayout } from './layout'
import { AccessDeniedPage, NotFoundPage } from './pages/States'
import { LoginPage, MfaPage, PasswordResetPage, PasswordResetConfirmPage, InvitationPage } from './pages/Access'
import { DashboardPage } from './pages/Dashboard'
import { AccountDetailPage, AccountsPage, UserDetailPage, UsersPage } from './pages/Accounts'
import { SubscriptionDetailPage, SubscriptionsPage } from './pages/Subscriptions'
import { BetaPage } from './pages/Beta'
import { PlanDetailPage, PlansPage } from './pages/Plans'
import { TrialsRetentionPage } from './pages/TrialsRetention'
import { ChargesPage, ChargeDetailPage } from './pages/Charges'
import { IntegrationsPage } from './pages/Integrations'
import { OperationsPage } from './pages/Operations'
import { ReceivablesPage } from './pages/Receivables'
import { NewSupportTicketPage, SupportPage, SupportTicketDetailPage } from './pages/Support'
import { AuditPage } from './pages/Audit'
import { ProfilePage, PreferencesPage, AboutPage } from './pages/Personal'
import { SecurityPage } from './pages/Security'
import { NotificationsPage } from './pages/Notifications'
import { ContactsPage } from './pages/Contacts'
import { TeamPage } from './pages/Team'
import { FaqPage } from './pages/Faq'
import { SupportContactPage } from './pages/SupportContact'
import { LegalDocumentsPage } from './pages/LegalDocuments'
import { canManageTeam } from './lib/accessPolicy'

function Protected({ children }: { children: React.ReactNode }) {
  const auth = useAuth(); const location = useLocation()
  if (auth.status === 'loading') return <div className="fullscreen-state"><Loading label="Validando sessão…" /></div>
  if (auth.status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return children
}
function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  return canManageTeam(user) ? children : <AccessDeniedPage />
}
function CapabilityOnly({ capability, children }: { capability: string; children: React.ReactNode }) {
  const { user } = useAuth()
  return user?.capabilities.includes(capability) ? children : <AccessDeniedPage />
}
function AnonymousOnly({ children }: { children: React.ReactNode }) {
  return useAuth().status === 'authenticated' ? <Navigate to="/" replace /> : children
}

export function AppRoutes() {
  return <Routes>
    <Route path="/login" element={<AnonymousOnly><LoginPage /></AnonymousOnly>} />
    <Route path="/mfa" element={<AnonymousOnly><MfaPage /></AnonymousOnly>} />
    <Route path="/recuperar-acesso" element={<PasswordResetPage />} />
    <Route path="/nova-senha" element={<PasswordResetConfirmPage />} />
    <Route path="/aceitar-convite" element={<InvitationPage />} />
    <Route path="/" element={<Protected><AdminLayout /></Protected>}>
      <Route index element={<DashboardPage />} />
      <Route path="equipe" element={<AdminOnly><TeamPage /></AdminOnly>} />
      <Route path="juridico" element={<CapabilityOnly capability="legal.manage"><LegalDocumentsPage /></CapabilityOnly>} />
      <Route path="contas" element={<CapabilityOnly capability="accounts.read"><AccountsPage /></CapabilityOnly>} />
      <Route path="contas/:accountId" element={<CapabilityOnly capability="accounts.read"><AccountDetailPage /></CapabilityOnly>} />
      <Route path="usuarios" element={<CapabilityOnly capability="accounts.read"><UsersPage /></CapabilityOnly>} />
      <Route path="usuarios/:userId" element={<CapabilityOnly capability="accounts.read"><UserDetailPage /></CapabilityOnly>} />
      <Route path="assinaturas" element={<CapabilityOnly capability="subscriptions.read"><SubscriptionsPage /></CapabilityOnly>} />
      <Route path="assinaturas/:subscriptionId" element={<CapabilityOnly capability="subscriptions.read"><SubscriptionDetailPage /></CapabilityOnly>} />
      <Route path="beta" element={<CapabilityOnly capability="accounts.read"><BetaPage /></CapabilityOnly>} />
      <Route path="planos" element={<CapabilityOnly capability="plans.manage"><PlansPage /></CapabilityOnly>} />
      <Route path="planos/:planId" element={<CapabilityOnly capability="plans.manage"><PlanDetailPage /></CapabilityOnly>} />
      <Route path="cobrancas" element={<CapabilityOnly capability="charges.read"><ChargesPage /></CapabilityOnly>} />
      <Route path="cobrancas/:orderId" element={<CapabilityOnly capability="charges.read"><ChargeDetailPage /></CapabilityOnly>} />
      <Route path="recebiveis" element={<CapabilityOnly capability="finance.read"><ReceivablesPage /></CapabilityOnly>} />
      <Route path="operacao" element={<CapabilityOnly capability="operations.read"><OperationsPage /></CapabilityOnly>} />
      <Route path="faq" element={<CapabilityOnly capability="support.manage"><FaqPage /></CapabilityOnly>} />
      <Route path="contato-suporte" element={<CapabilityOnly capability="support.manage"><SupportContactPage /></CapabilityOnly>} />
      <Route path="atendimento" element={<CapabilityOnly capability="support.manage"><SupportPage /></CapabilityOnly>} />
      <Route path="atendimento/novo" element={<CapabilityOnly capability="support.manage"><NewSupportTicketPage /></CapabilityOnly>} />
      <Route path="atendimento/:ticketId" element={<CapabilityOnly capability="support.manage"><SupportTicketDetailPage /></CapabilityOnly>} />
      <Route path="trials-retencao" element={<CapabilityOnly capability="accounts.read"><TrialsRetentionPage /></CapabilityOnly>} />
      <Route path="integracoes" element={<CapabilityOnly capability="integrations.manage"><IntegrationsPage /></CapabilityOnly>} />
      <Route path="auditoria" element={<CapabilityOnly capability="audit.read"><AuditPage /></CapabilityOnly>} />
      <Route path="meu-usuario" element={<ProfilePage />} />
      <Route path="preferencias" element={<PreferencesPage />} />
      <Route path="seguranca" element={<SecurityPage />} />
      <Route path="notificacoes" element={<NotificationsPage />} />
      <Route path="contatos" element={<CapabilityOnly capability="support.manage"><ContactsPage /></CapabilityOnly>} />
      <Route path="sobre" element={<AboutPage />} />
      <Route path="sem-permissao" element={<AccessDeniedPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
}
